import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, DropdownButton, Dropdown } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { RotateLoader } from "react-spinners";

import { FaTimes } from "react-icons/fa";
import { GoSearch } from "react-icons/go";
import { AiOutlineSortAscending } from "react-icons/ai";
import { AuthContext } from "../../context/main/auth-context";
import "../../css/orders/orders.css";

import getAllOpenOrdersManualAdded from "../../components/orders/get-all-open-orders-manual-added";
import OrdersHeader from "../../components/orders/orders-header";
import OrderListItemOpen from "../../components/orders/order-list-item-open";
import AssistantToggleButton from "../../components/main/assistant-toggle-button";

const OrdersOpenManualAdded = (props) => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  // container ref om checkbox-selectie te scopen tot huidig scherm
  const listContainerRef = useRef(null);

  const sortingNameHandler = (input) => {
    if (input === `{"orderItems.product.productData.sku":1}`) {
      return "Productgroep (SKU)";
    } else if (input === `{"orderPlacedDateTime":1}`) {
      return "Besteldatum (Oud - Nieuw)";
    } else {
      return "Besteldatum (Nieuw - Oud)";
    }
  };
  const sortingHandler = () => {
    let sortingOrders = JSON.parse(sessionStorage.getItem("openOrdersSorting"));
    if (sortingOrders) return sortingOrders;
    return `{"orderPlacedDateTime":-1}`;
  };

  const sortOrders = (sorting) => {
    if (sorting) {
      sessionStorage.setItem("openOrdersSorting", JSON.stringify(sorting));
    }
    setSortOrdersFilter(sorting);
    setCurrentPage(0);
    setOffset(0);
  };

  // ---------- State ----------
  const [errorMessage, setErrorMessage] = useState("");
  const [openOrders, setOpenOrders] = useState([]);
  const [loadingState, setLoadingState] = useState("");

  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [offset, setOffset] = useState(0);
  const [perPage, setPerPage] = useState(25);

  const [ordersToShow, setOrdersToShow] = useState([]);
  const [filteredCount, setFilteredCount] = useState(0);

  const [checkedOrders, setCheckedOrders] = useState([]);
  const [recountOrders] = useState(false); // behouden voor OrdersHeader prop

  const [loadingPage] = useState("");
  const [showAlertText] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrdersFilter, setSortOrdersFilter] = useState(sortingHandler);

  // ---------- Data ophalen ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingState("Bestellingen worden geladen");
      setErrorMessage("");
      try {
        const res = await getAllOpenOrdersManualAdded(auth.token, sortOrdersFilter);
        if (cancelled) return;

        if (res && res.orders && res.orders.length > 0) {
          setOpenOrders(res.orders);
        } else {
          setOpenOrders([]);
          setErrorMessage("Geen openstaande orders gevonden");
        }
      } catch (e) {
        setOpenOrders([]);
        setErrorMessage("Fout bij laden van bestellingen");
      } finally {
        if (!cancelled) setLoadingState("");
      }
    })();
    return () => { cancelled = true; };
  }, [auth.token, sortOrdersFilter]);

  // ---------- Zoek: veldgericht ----------
  const searched = useMemo(() => {
    if (!openOrders?.length) return [];
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return openOrders;

    const hit = (o) => {
      if (String(o?.orderId || "").toLowerCase().includes(q)) return true;

      const sd = o?.shipmentDetails || {};
      const name = `${sd.firstName || ""} ${sd.surname || ""}`.trim().toLowerCase();
      if (name.includes(q)) return true;
      if ((sd.zipCode || "").toLowerCase().includes(q)) return true;
      if ((sd.city || "").toLowerCase().includes(q)) return true;
      if ((sd.streetName || "").toLowerCase().includes(q)) return true;

      if (o?.orderItems?.some((oi) => {
        const p = oi?.product || {};
        const pd = p?.productData || {};
        const title = (p.title || pd.title || "").toLowerCase();
        const sku = (pd.sku || "").toLowerCase();
        const ean = (p.ean || pd.ean || "").toLowerCase();
        return title.includes(q) || sku.includes(q) || ean.includes(q);
      })) return true;

      return false;
    };

    return openOrders.filter(hit);
  }, [openOrders, searchQuery]);

  // ---------- Paginatie ----------
  useEffect(() => {
    const total = searched.length;
    setFilteredCount(total);

    const pc = Math.max(1, Math.ceil(total / perPage));
    const safePage = Math.min(currentPage, pc - 1);
    const start = safePage * perPage;

    setPageCount(pc);
    setCurrentPage(safePage);
    setOffset(start);
    setOrdersToShow(searched.slice(start, start + perPage));
  }, [searched, perPage, currentPage]);

useEffect(() => {
  setCurrentPage(0);
  setOffset(0);
  setCheckedOrders([]); // ✅ selectie ook leegmaken bij nieuwe zoekopdracht
}, [searchQuery]);

  // ---------- Handlers ----------
const handlePageClick = (e) => {
  // reset selectie
  setCheckedOrders([]);

  const selectedPage = e.selected;
  const newOffset = selectedPage * perPage;

  setCurrentPage(selectedPage);
  setOffset(newOffset);
};


  const selectionText = () => `(${checkedOrders.length || 0})`;

const handleCheckedAll = (e) => {
  if (!ordersToShow || ordersToShow.length === 0) return;

  const scope = listContainerRef.current;
  if (!scope) return;

  const boxes = Array.from(scope.querySelectorAll(".o-li-item input"));

  if (e.target.checked) {
    // Alles aanvinken → laat elke checkbox zelf handleChecked triggeren
    boxes.forEach((checkbox) => {
      if (!checkbox.checked) checkbox.click();
    });
  } else {
    // Alles uitvinken
    boxes.forEach((checkbox) => {
      if (checkbox.checked) checkbox.click();
    });
  }
};



const handleChecked = (isChecked, order, orderItem) => {
  const key = String(order.id || order.orderId || order._id);

  setCheckedOrders((prev) => {
    // AANVINKEN → order toevoegen als die nog niet in de lijst zit
    if (isChecked) {
      if (prev.some((o) => String(o.id || o.orderId || o._id) === key)) {
        return prev; // staat er al in
      }
      return [...prev, order];
    }

    // UITVINKEN → order verwijderen
    return prev.filter(
      (o) => String(o.id || o.orderId || o._id) !== key
    );
  });
};


  const shipOrdersHandler = (option, order) => {
    const orders = order ? [order] : checkedOrders;
    sessionStorage.setItem("checkedOrders", JSON.stringify(orders));
    sessionStorage.setItem("shippingMethod", JSON.stringify(option));
    navigate("/orders/manual-added/ship/step-1");
  };

  return (
    <React.Fragment>
      <div className="title-header title-header-open-orders">
        <span>Bestellingen</span>
        <div>
          <Button
            onClick={() => {
              sessionStorage.removeItem("createOrder");
              navigate("/orders/create-order");
            }}
          >
            Nazending aanmaken
          </Button>
        </div>
      </div>

      <OrdersHeader recountOrders={recountOrders} />

      <div className="orders-sub-header">
        <div className="orders-sub-header-filter-count">
          ({filteredCount}) <span>bestellingen gefilterd</span>
        </div>

        <div className="orders-search-wrapper">
          <label className="input-search-icon">
            <GoSearch />
          </label>
          <input
            type="search"
            name="search-form"
            id="search-form"
            className="search-input"
            placeholder="Zoek bestelling..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="orders-sub-header-filter-options">
          <DropdownButton
            className="filter-open-orders-dropdown"
            align="end"
            id="dropdown-sorting-button"
            title={<AiOutlineSortAscending />}
            onSelect={(e) => { sortOrders(e); }}
          >
            <Dropdown.Item key={89} eventKey={'{"orderItems.product.productData.sku":1}'}>
              Productgroep (SKU)
            </Dropdown.Item>
            <Dropdown.Item key={90} eventKey={'{"orderPlacedDateTime":-1}'}>
              Besteldatum (Nieuw - Oud)
            </Dropdown.Item>
            <Dropdown.Item key={91} eventKey={'{"orderPlacedDateTime":1}'}>
              Besteldatum (Oud - Nieuw)
            </Dropdown.Item>
          </DropdownButton>
        </div>
      </div>

      {sortOrdersFilter && sortOrdersFilter !== `{"orderPlacedDateTime":-1}` && (
        <div className="orders-sub-header-filters">
          <div className="orders-sub-header-filters-title">Sortering:</div>
          <div className="orders-sub-header-filters-item">
            <div>{sortingNameHandler(sortOrdersFilter)}</div>
            <div className="orders-sub-header-filters-item-close">
              <FaTimes
                onClick={() => {
                  sessionStorage.removeItem("openOrdersSorting");
                  setSortOrdersFilter(`{"orderPlacedDateTime":-1}`);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {ordersToShow && (
        <div className="orders-container-header open-orders-container-header">
          <div className="checkbox-all-orders">
            <Form.Check
              type="checkbox"
              label={selectionText()}
              onClick={handleCheckedAll}
              disabled={!ordersToShow || ordersToShow.length === 0}
            />
          </div>

          {checkedOrders.length > 0 && (
            <div className="orders-container-header-actions">
              <Button onClick={() => shipOrdersHandler("TNT_BRIEF")}>
                PostNL Briefpost
              </Button>
              <Button onClick={() => shipOrdersHandler("POSTNL_3085")}>
                PostNL Pakket Standaard
              </Button>
              <Button onClick={() => shipOrdersHandler("POSTNL_2928")}>
                PostNL Brievenbuspakje
              </Button>
            </div>
          )}
        </div>
      )}

      {!!errorMessage && (
        <div className="noOrdersContainer">
          <div className="noOrdersText">
            <div className="no-result-search">
              <GoSearch />
              {errorMessage}
            </div>
          </div>
        </div>
      )}

      <div
        className="orders-container open-orders-container"
        id="open-orders-container"
        ref={listContainerRef}
      >
        {ordersToShow &&
          ordersToShow.length > 0 &&
          ordersToShow.map((order) => {
            const key = order.orderId || order._id || order.id;
            return (
              <div
                key={key}
                className={
                  "o-li-item-container" +
                  (order.orderItems && order.orderItems.length > 1 ? " multiOrderItems" : "")
                }
              >
                <OrderListItemOpen
                  order={order}
                  handleChecked={handleChecked}
                />
              </div>
            );
          })}

        {ordersToShow && ordersToShow.length === 0 && !loadingState && (
          <div className="noOrdersContainer">
            <div className="noOrdersText">
              {searchQuery === "" ? (
                "Alle orders zijn ingepakt, goed zo!"
              ) : (
                <div className="no-result-search">
                  <GoSearch />
                  Geen resultaat
                </div>
              )}
            </div>
          </div>
        )}

        {loadingState && (
          <div className="loading-container">
            <div className="loading-container-title">{loadingState}</div>
            <RotateLoader
              size={100}
              width={100}
              height={10}
              color={"#fff"}
              loading={!!loadingState}
            />
          </div>
        )}
      </div>

      {ordersToShow && filteredCount > 0 && (
        <ReactPaginate
          previousLabel={"<"}
          nextLabel={">"}
          breakLabel={"..."}
          breakClassName={"break-me"}
          pageCount={pageCount}
          marginPagesDisplayed={5}
          pageRangeDisplayed={5}
          onPageChange={handlePageClick}
          containerClassName={"pagination"}
          activeClassName={"active"}
          forcePage={currentPage}
        />
      )}

      <AssistantToggleButton />
    </React.Fragment>
  );
};

export default OrdersOpenManualAdded;